import { ButtonLink } from "@ui/ButtonLink";
import { Icon } from "@ui/Icon";
import askGVTLogoSmall from "@ui/icons/AskGVTLogoSmall.svg?raw";
import gvtStudioLogoSmall from "@ui/icons/GVTStudioLogoSmall.svg?raw";
import { useAppContext } from "@ui/layouts/layoutProvider";
import { PlatformSwitch } from "@ui/layouts/PlatformSwitch";
import { PlatformTabs } from "@ui/layouts/PlatformTabs";
import { pickerMode } from "@ui/layouts/pickerMode";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import { Show } from "solid-js";
import "./MenuPrimaryActions.css";

const SWAP_VIEWBOX = "0 -960 960 960";
const SWAP_ARROW_LEFT =
	"m194-323 100 100q9 9 9 21t-9 21q-9 9-21 9t-21-9L101-332q-5-5-7-10t-2-11q0-6 2-11t7-10l151-151q9-9 21-9t21 9q9 9 9 21t-9 21L194-383h286q13 0 21.5 8.5T510-353q0 13-8.5 21.5T480-323H194Z";
const SWAP_ARROW_RIGHT =
	"M766-577H480q-13 0-21.5-8.5T450-607q0-13 8.5-21.5T480-637h286L666-737q-9-9-9-21t9-21q9-9 21-9t21 9l151 151q5 5 7 10t2 11q0 6-2 11t-7 10L708-435q-9 9-21 9t-21-9q-9-9-9-21t9-21l100-100Z";

const SwapHorizIcon = () => (
	<span class="platform-switcher__swap-icon" aria-hidden="true">
		<svg class="platform-switcher__arrow-right" viewBox={SWAP_VIEWBOX}>
			<path d={SWAP_ARROW_RIGHT} fill="currentColor" />
		</svg>
		<svg class="platform-switcher__arrow-left" viewBox={SWAP_VIEWBOX}>
			<path d={SWAP_ARROW_LEFT} fill="currentColor" />
		</svg>
	</span>
);

type SwitcherTarget = "studio" | "askgvt";

const switcher = {
	studio: {
		href: "/studio",
		label: "Go to Creator studio",
		mark: gvtStudioLogoSmall,
	},
	askgvt: {
		href: "/",
		label: "Go to AskGVT",
		mark: askGVTLogoSmall,
	},
} as const;

type Props = {
	cta: { href: string; label: string; symbol: string };
	/** Product the switcher takes the user to. */
	switchTo: SwitcherTarget;
};

/** Main CTA plus the AskGVT ↔ Studio switcher, as in the approved sidebar. */
export const MenuPrimaryActions = (props: Props) => {
	const { menuIsOpen } = useAppContext();
	const target = () => switcher[props.switchTo];
	const mode = pickerMode();

	return (
		<>
			<Show when={mode === "switch"}>
				<PlatformSwitch />
			</Show>
			<Show when={mode === "tabs"}>
				<PlatformTabs />
			</Show>
			<div class="menu-primary-actions">
				<TooltipTrigger
					placement="right"
					anchorTo=".side-bar"
					disabled={menuIsOpen()}
				>
					<ButtonLink
						href={props.cta.href}
						variant="primary"
						theme="mint-grad"
						symbol={props.cta.symbol}
						size="large"
						fullWidth
						class="menu-primary-actions__cta"
						aria-label={props.cta.label}
					>
						{props.cta.label}
					</ButtonLink>
					<Tooltip>{props.cta.label}</Tooltip>
				</TooltipTrigger>
				<Show when={mode === "button"}>
					<TooltipTrigger
						placement="right"
						anchorTo=".side-bar"
						disabled={menuIsOpen()}
					>
						<ButtonLink
							size="large"
							variant="tertiary"
							href={target().href}
							class="platform-switcher"
							aria-label={target().label}
						>
							<Icon
								icon={target().mark}
								fillWithCurrentColor={false}
								class="platform-switcher__mark"
								display="inline-flex"
							/>
							<span class="platform-switcher__swap">
								<SwapHorizIcon />
							</span>
						</ButtonLink>
						<Tooltip>{target().label}</Tooltip>
					</TooltipTrigger>
				</Show>
			</div>
		</>
	);
};
